import { useState, useEffect, useCallback } from 'react';
import { getShareContacts, sharePostToDM } from '../APIs/chatApi';
import { trackEvent } from '../utils/analytics';

const useShareToDM = (postId, onClose) => {
  const [contacts,     setContacts]     = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [query,        setQuery]        = useState('');
  const [selected,     setSelected]     = useState([]);
  const [optionalText, setOptionalText] = useState('');
  const [loading,      setLoading]      = useState(true);
  const [sending,      setSending]      = useState(false);
  const [sentCount,    setSentCount]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getShareContacts();
        if (!cancelled) {
          setContacts(res.data.contacts || []);
          setFiltered(res.data.contacts || []);
        }
      } catch {
        if (!cancelled) { setContacts([]); setFiltered([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setFiltered(contacts);
    } else {
      const q = query.toLowerCase();
      setFiltered(contacts.filter(c => c.username.toLowerCase().includes(q)));
    }
  }, [query, contacts]);

  const toggleSelect = useCallback((userId) => {
    setSelected(prev => {
      if (prev.includes(userId)) return prev.filter(id => id !== userId);
      if (prev.length >= 10) return prev;
      return [...prev, userId];
    });
  }, []);

  const handleSend = useCallback(async () => {
    // sentCount !== null guard prevents double-send after success
    if (!selected.length || sending || sentCount !== null) return;
    setSending(true);
    try {
      const res = await sharePostToDM(postId, selected, optionalText);
      const count = res.data?.sent ?? selected.length;
      setSentCount(count);
      trackEvent('dm_share', { postId, metadata: { recipient_count: count } });
      setTimeout(() => {
        onClose?.();
        setSentCount(null);
        setSelected([]);
        setOptionalText('');
      }, 1400);
    } catch {
      // silent — UI stays open on failure
    } finally {
      setSending(false);
    }
  }, [postId, selected, optionalText, sending, sentCount, onClose]);

  return {
    filtered, query, setQuery,
    selected, toggleSelect,
    optionalText, setOptionalText,
    loading, sending, sentCount,
    handleSend,
  };
};

export default useShareToDM;
